class ApiClient {
    constructor(baseURL) {
        this.token = null;
        this.unauthorizedFiredAt = 0;
        this.auth = {
            login: (credentials) => this.request('/auth/login', {
                method: 'POST',
                body: credentials
            }),
            register: (userData) => this.request('/auth/register', {
                method: 'POST',
                body: userData
            }),
            me: () => this.request('/auth/me')
        };
        this.admin = {
            getOrders: (params) => this.get('/admin/orders', { params }),
            updateOrderStatus: (orderId, status, notes, photo) => this.request(`/admin/orders/${orderId}/status`, {
                method: 'PUT',
                body: { status, notes, photo }
            }),
            assignUser: (orderId, payload) => this.request(`/admin/orders/${orderId}/assign`, {
                method: 'POST',
                body: payload
            }),
            getUsers: (params) => this.request('/admin/users' + (params ? '?' + new URLSearchParams(params).toString() : '')),
            getStats: () => this.request('/admin/stats'),
            getNavigationCounts: () => this.request('/admin/navigation/counts'),
            // =============== УПРАВЛЕНИЕ ЦВЕТАМИ ===============
            flowers: {
                getAll: (params) => this.request('/admin/flowers' + (params ? '?' + new URLSearchParams(params).toString() : '')),
                getById: (id) => this.request(`/admin/flowers/${id}`),
                create: (data) => this.request('/admin/flowers', {
                    method: 'POST',
                    body: data
                }),
                update: (id, data) => this.request(`/admin/flowers/${id}`, {
                    method: 'PUT',
                    body: data
                }),
                delete: (id) => this.request(`/admin/flowers/${id}`, {
                    method: 'DELETE'
                }),
                updateStock: (id, quantity) => this.request(`/admin/flowers/${id}/stock`, {
                    method: 'PUT',
                    body: { quantity }
                })
            },
            // =============== УПРАВЛЕНИЕ БУКЕТАМИ ===============
            bouquets: {
                getAll: (params) => this.request('/admin/bouquets' + (params ? '?' + new URLSearchParams(params).toString() : '')),
                getById: (id) => this.request(`/admin/bouquets/${id}`),
                create: (data) => this.request('/admin/bouquets', {
                    method: 'POST',
                    body: data
                }),
                update: (id, data) => this.request(`/admin/bouquets/${id}`, {
                    method: 'PUT',
                    body: data
                }),
                delete: (id) => this.request(`/admin/bouquets/${id}`, {
                    method: 'DELETE'
                })
            },
            // =============== УПРАВЛЕНИЕ КАТЕГОРИЯМИ ===============
            categories: {
                getAll: () => this.request('/admin/categories'),
                getById: (id) => this.request(`/admin/categories/${id}`),
                create: (data) => this.request('/admin/categories', {
                    method: 'POST',
                    body: data
                }),
                update: (id, data) => this.request(`/admin/categories/${id}`, {
                    method: 'PUT',
                    body: data
                }),
                delete: (id) => this.request(`/admin/categories/${id}`, {
                    method: 'DELETE'
                }),
                getBouquets: (id) => this.request(`/admin/categories/${id}/bouquets`),
                updateBouquets: (id, bouquetIds) => this.request(`/admin/categories/${id}/bouquets`, {
                    method: 'PUT',
                    body: { bouquetIds }
                })
            },
            // =============== УПРАВЛЕНИЕ ЧАТАМИ ===============
            chats: {
                getAll: (params) => this.request('/admin/chats' + (params ? '?' + new URLSearchParams(params).toString() : '')),
                getById: (id) => this.request(`/admin/chats/${id}`),
                getMessages: (chatId, params) => this.request(`/admin/chats/${chatId}/messages` + (params ? '?' + new URLSearchParams(params).toString() : '')),
                sendMessage: (chatId, content, type = 'text') => this.request(`/admin/chats/${chatId}/messages`, {
                    method: 'POST',
                    body: { content, type }
                }),
                updateStatus: (chatId, payload) => this.request(`/admin/chats/${chatId}/status`, {
                    method: 'PUT',
                    body: payload
                }),
                markAsRead: (chatId) => this.request(`/admin/chats/${chatId}/read`, {
                    method: 'PUT'
                })
            },
            // =============== УПРАВЛЕНИЕ УВЕДОМЛЕНИЯМИ ===============
            notifications: {
                getAll: (params) => {
                    const queryParams = new URLSearchParams();
                    if (params?.page)
                        queryParams.append('page', params.page.toString());
                    if (params?.limit)
                        queryParams.append('limit', params.limit.toString());
                    if (params?.category)
                        queryParams.append('category', params.category);
                    if (params?.isRead !== undefined)
                        queryParams.append('isRead', params.isRead.toString());
                    if (params?.isUrgent !== undefined)
                        queryParams.append('isUrgent', params.isUrgent.toString());
                    const queryString = queryParams.toString();
                    return this.request('/admin/notifications' + (queryString ? `?${queryString}` : ''));
                },
                markAsRead: (notificationId) => this.request(`/admin/notifications/${notificationId}/read`, {
                    method: 'POST'
                }),
                markAllAsRead: (category) => this.request('/admin/notifications/read-all', {
                    method: 'POST',
                    body: { category }
                }),
                delete: (notificationId) => this.request(`/admin/notifications/${notificationId}`, {
                    method: 'DELETE'
                })
            }
        };
        this.bouquets = {
            getAll: (params) => this.request('/bouquets' + (params ? '?' + new URLSearchParams(params).toString() : '')),
            getById: (id) => this.request(`/bouquets/${id}`),
            getCategories: () => this.request('/bouquets/categories/all')
        };
        this.orders = {
            create: (orderData) => this.request('/orders', {
                method: 'POST',
                body: orderData
            }),
            getMyOrders: (params) => this.get('/orders', { params }),
            getTracking: (id) => this.request(`/orders/${id}/tracking`)
        };
        this.baseURL = baseURL;
    }
    setToken(token) {
        this.token = token;
    }
    setUnauthorizedHandler(handler) {
        this.onUnauthorized = handler;
    }
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const config = {
            method: options.method || 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(this.token && { Authorization: `Bearer ${this.token}` }),
                ...options.headers
            }
        };
        if (options.signal) {
            config.signal = options.signal;
        }
        if (options.body) {
            config.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
        }
        try {
            const response = await fetch(url, config);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: response.statusText }));
                // Handle 401 Unauthorized - trigger logout (but NOT for auth endpoints)
                if (response.status === 401) {
                    // Skip unauthorized handler for login/register endpoints
                    // 401 on these endpoints means "wrong credentials", not "session expired"
                    const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register');
                    if (!isAuthEndpoint) {
                        // CRITICAL: Clear token SYNCHRONOUSLY before handler fires.
                        // This prevents any in-flight or queued requests from retrying
                        // with the same dead token (race condition root cause).
                        this.token = null;
                        const now = Date.now();
                        // Debounce: fire handler at most once per 2 seconds to avoid
                        // repeated logout/redirect from concurrent 401 responses
                        if (this.onUnauthorized && now - this.unauthorizedFiredAt > 2000) {
                            this.unauthorizedFiredAt = now;
                            console.warn('[ApiClient] 401 Unauthorized - token expired or invalid');
                            this.onUnauthorized();
                        }
                    }
                    // Preserve backend error message and attach status so React Query
                    // retry logic can detect 401 without relying on the ApiError class
                    const authError = new Error(errorData.error || errorData.message || 'Authentication required');
                    authError.response = { status: 401, statusText: 'Unauthorized', data: errorData };
                    throw authError;
                }
                // ⚠️ FIX: Сохраняем полную структуру error.response для RateLimitError handling
                const error = new Error(errorData.error?.message || errorData.message || errorData.error || `HTTP ${response.status}`);
                error.response = {
                    status: response.status,
                    statusText: response.statusText,
                    data: errorData
                };
                throw error;
            }
            // Handle 204 No Content - return null/undefined as T
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return undefined;
            }
            // Try to parse JSON, but handle empty responses gracefully
            const text = await response.text();
            if (!text) {
                return undefined;
            }
            try {
                return JSON.parse(text);
            }
            catch (error) {
                console.warn('[ApiClient] Failed to parse JSON response:', error);
                return undefined;
            }
        }
        catch (error) {
            if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
                throw error;
            }
            const errorMessage = error instanceof Error
                ? error.message
                : typeof error === 'string'
                    ? error
                    : '';
            const isExpectedIdempotentError = errorMessage.includes('Resource already exists') || errorMessage.includes('409');
            if (!isExpectedIdempotentError) {
                console.error('API Request failed:', error);
            }
            throw error;
        }
    }
    // =============== ПУБЛИЧНЫЕ HTTP МЕТОДЫ ===============
    // Добавлены для прямого использования в хуках и сервисах
    /**
     * Выполнить GET-запрос
     * @param endpoint - URL endpoint (например, '/admin/florist/pending-orders')
     * @param options - Опции запроса (params для query string, signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    get(endpoint, options) {
        let url = endpoint;
        if (options?.params) {
            // Фильтруем undefined/null и поддерживаем массивы как repeated params: ?status=A&status=B
            const queryParams = new URLSearchParams();
            for (const [key, value] of Object.entries(options.params)) {
                if (value === undefined || value === null)
                    continue;
                if (Array.isArray(value)) {
                    for (const item of value) {
                        if (item !== undefined && item !== null) {
                            queryParams.append(key, String(item));
                        }
                    }
                }
                else {
                    queryParams.set(key, String(value));
                }
            }
            const queryString = queryParams.toString();
            if (queryString) {
                url = `${endpoint}?${queryString}`;
            }
        }
        return this.request(url, {
            method: 'GET',
            signal: options?.signal
        });
    }
    /**
     * Выполнить POST-запрос
     * @param endpoint - URL endpoint
     * @param data - Данные для отправки в body
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    post(endpoint, data, options) {
        return this.request(endpoint, {
            method: 'POST',
            body: data,
            signal: options?.signal
        });
    }
    /**
     * Выполнить PUT-запрос
     * @param endpoint - URL endpoint
     * @param data - Данные для отправки в body
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    put(endpoint, data, options) {
        return this.request(endpoint, {
            method: 'PUT',
            body: data,
            signal: options?.signal
        });
    }
    /**
     * Выполнить PATCH-запрос
     * @param endpoint - URL endpoint
     * @param data - Данные для отправки в body
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    patch(endpoint, data, options) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: data,
            signal: options?.signal
        });
    }
    /**
     * Выполнить DELETE-запрос
     * @param endpoint - URL endpoint
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    delete(endpoint, options) {
        return this.request(endpoint, {
            method: 'DELETE',
            signal: options?.signal
        });
    }
}
// Экспортируем только класс - каждое приложение создает свой экземпляр
export { ApiClient };
